import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CategoryPermissions.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const CategoryPermissions = ({ permissionGroups = {}, refreshPermissions, refreshAdminData }) => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    // NAVIGATION KATEGORIEN MIT UNTERPUNKTEN
    // WICHTIG: Diese Kategorien werden automatisch erkannt und müssen nicht in der DB existieren!
    const navigationCategories = [
        {
            id: 'support',
            name: '🛟 Support Area',
            color: '#00ffc6',
            description: 'Zugriff auf Support-Bereich',
            permissions: ['manage_support'],
            items: ['Dashboard', 'Self Info', 'Team Liste', 'Support Fälle', 'Ankündigungen']
        },
        {
            id: 'team-management',
            name: '👑 Teamleitung',
            color: '#f59f00',
            description: 'Zugriff auf Teamleitung-Bereich',
            permissions: ['team_view', 'manage_meetings', 'manage_team_members', 'view_whitelist_stats', 'manage_absences', 'view_team_management'],
            items: ['Besprechungen', 'Teamverwaltung', 'WL-Statistiken', 'Abwesenheiten', 'Support Performance']
        },
        {
            id: 'admin',
            name: '⚡ Admin Area',
            color: '#ff6b6b',
            description: 'Zugriff auf Admin-Bereich',
            permissions: ['manage_permissions', 'admin_access', 'manage_system', 'view_admin_panel', 'view_logs', 'manage_roles'],
            items: ['Admin Panel', 'Server-Einstellungen', 'Audit Logs', 'Ankündigung senden']
        },
        {
            id: 'analyse',
            name: '🔍 Analyse',
            color: '#007bff',
            description: 'Zugriff auf Analyse-Bereich',
            permissions: ['view_analysis'],
            items: ['Analysieren', 'Fehlende Clips']
        },
        {
            id: 'faction-management',
            name: '⚔️ Fraktionsverwaltung',
            color: '#9b59b6',
            description: 'Zugriff auf Fraktionsverwaltung',
            permissions: ['view_faction_overview'],
            items: ['Fraktionsübersicht']
        },
        {
            id: 'verwaltung',
            name: '🏢 Verwaltungen',
            color: '#8B4513',
            description: 'Zugriff auf Verwaltungsbereich',
            permissions: ['manage_refunds'],
            items: ['Rückerstattungen']
        },
        {
            id: 'streaming',
            name: '📺 Streaming',
            color: '#9146FF',
            description: 'Zugriff auf Streaming-Bereich',
            permissions: ['manage_streamers'],
            items: ['Streamer verwalten']
        },
        {
            id: 'extra-modules',
            name: '🧩 Zusatz Module',
            color: '#ff6b6b',
            description: 'Zugriff auf Zusatz Module',
            permissions: ['view_extra_modules', 'manage_giveaways', 'manage_polls', 'manage_automod', 'view_user_check'],
            items: ['Giveaway', 'Umfragen', 'Automod', 'User Check']
        },
        {
            id: 'lite-dashboard',
            name: '🛬 Lite Dashboard',
            color: '#00ffc6',
            description: 'Zugriff auf das Lite-Dashboard (creator.communitypanel.de)',
            permissions: ['access_lite_dashboard'],
            items: ['User Check', 'Einreise']
        }
    ];

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoles(response.data);
            return response.data;
        } catch (err) {
            console.error('Fehler beim Laden der Rollen:', err);
            return [];
        }
    };

    const handleRoleSelect = (role) => {
        setSelectedRole(role);
        setSelectedPermissions(Array.isArray(role.permissions) ? role.permissions : []);
    };

    const togglePermission = (permissionName) => {
        setSelectedPermissions(prev => {
            if (prev.includes(permissionName)) {
                return prev.filter(p => p !== permissionName);
            } else {
                return [...prev, permissionName];
            }
        });
    };

    const toggleCategory = (category) => {
        const categoryPerms = category.permissions;
        const allSelected = categoryPerms.every(perm => selectedPermissions.includes(perm));

        if (allSelected) {
            setSelectedPermissions(prev => prev.filter(p => !categoryPerms.includes(p)));
        } else {
            setSelectedPermissions(prev => {
                const newPerms = [...prev];
                categoryPerms.forEach(perm => {
                    if (!newPerms.includes(perm)) newPerms.push(perm);
                });
                return newPerms;
            });
        }
    };

    const isCategoryFullySelected = (category) => category.permissions.every(perm => selectedPermissions.includes(perm));
    const isCategoryPartiallySelected = (category) => {
        const selectedCount = category.permissions.filter(perm => selectedPermissions.includes(perm)).length;
        return selectedCount > 0 && selectedCount < category.permissions.length;
    };

    const savePermissions = async () => {
        if (!selectedRole) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('discord_token');

            await axios.post(`${API_BASE}/api/set-role-permissions`, {
                roleId: selectedRole.id,
                permissions: selectedPermissions,
                merge: false
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setRoles(prevRoles => prevRoles.map(r => {
                if (r.id === selectedRole.id) {
                    return { ...r, permissions: selectedPermissions, permission_count: selectedPermissions.length };
                }
                return r;
            }));

            const response = await axios.get(`${API_BASE}/api/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoles(response.data);

            alert('✅ Gespeichert! Der User muss ggf. die Seite neu laden (F5).');
        } catch (err) {
            console.error('Speicherfehler:', err);
            alert('Fehler: ' + (err.response?.data?.error || 'Unbekannter Fehler'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-section" style={{ color: '#fff', fontFamily: 'sans-serif' }}>
            <div className="section-header" style={{ marginBottom: '20px' }}>
                <div>
                    <h3>🔐 Rollen-Berechtigungen verwalten</h3>
                    <p style={{ color: '#888', fontSize: '0.9em' }}>Weisen Sie Berechtigungen zu oder entfernen Sie sie von Rollen</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
                {/* Rollen-Liste */}
                <div className="card" style={{ background: '#222', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '15px', borderBottom: '1px solid #333', background: '#2a2a2a' }}>
                        <h4 style={{ margin: 0 }}>Rollen ({roles.length})</h4>
                    </div>
                    <div style={{ padding: '10px', maxHeight: '70vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {roles.map(role => (
                                <button
                                    key={role.id}
                                    onClick={() => handleRoleSelect(role)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '12px',
                                        background: selectedRole?.id === role.id ? '#007bff' : '#333',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    <span>{role.name}</span>
                                    <span style={{ fontSize: '0.75em', background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '10px' }}>
                                        {role.permission_count || 0}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Berechtigungen bearbeiten */}
                <div className="card" style={{ background: '#222', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '15px', borderBottom: '1px solid #333', background: '#2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>
                            {selectedRole ? `Berechtigungen für: ${selectedRole.name}` : 'Wähle eine Rolle aus'}
                        </h4>
                        {selectedRole && (
                            <button
                                onClick={savePermissions}
                                disabled={loading}
                                style={{
                                    padding: '8px 16px',
                                    background: '#28a745',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? '⏳ Speichert...' : '💾 Speichern'}
                            </button>
                        )}
                    </div>

                    <div style={{ padding: '20px' }}>
                        {!selectedRole ? (
                            <p style={{ color: '#888', textAlign: 'center', marginTop: '50px' }}>Wähle eine Rolle aus der Liste links aus.</p>
                        ) : (
                            <div>
                                <div style={{ marginBottom: '20px', padding: '15px', background: '#1a1a1a', borderRadius: '8px', borderLeft: '4px solid #007bff' }}>
                                    <p style={{ margin: 0, fontSize: '0.85em', color: '#ccc' }}>
                                        <strong>Tipp:</strong> Klicke auf eine Kategorie-Karte, um alle Punkte dieser Gruppe zu schalten.
                                    </p>
                                    <p style={{ margin: '5px 0 0 0', fontSize: '0.85em', color: '#00ffc6' }}>
                                        Ausgewählt: <strong>{selectedPermissions.length}</strong> Berechtigungen
                                    </p>
                                </div>

                                <h4 style={{ marginBottom: '15px' }}>📍 Navigation-Bereiche</h4>
                                <div style={{ display: 'grid', gap: '15px' }}>
                                    {navigationCategories.map(category => {
                                        const isFullySelected = isCategoryFullySelected(category);
                                        const isPartiallySelected = isCategoryPartiallySelected(category);
                                        const selectedCount = category.permissions.filter(p => selectedPermissions.includes(p)).length;

                                        return (
                                            <div key={category.id} style={{
                                                border: `1px solid ${isFullySelected ? category.color : '#444'}`,
                                                borderRadius: '8px',
                                                background: isFullySelected ? `${category.color}10` : '#1a1a1a'
                                            }}>
                                                <div
                                                    onClick={() => toggleCategory(category)}
                                                    style={{ padding: '15px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${category.color}`,
                                                            background: isFullySelected ? category.color : isPartiallySelected ? '#666' : 'transparent',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px'
                                                        }}>
                                                            {isFullySelected && '✓'}
                                                            {isPartiallySelected && '−'}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 'bold', color: isFullySelected ? category.color : '#fff' }}>{category.name}</div>
                                                            <div style={{ fontSize: '0.75em', color: '#888' }}>{category.description}</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right', fontWeight: 'bold', color: isFullySelected ? category.color : '#888' }}>
                                                        {selectedCount}/{category.permissions.length}
                                                    </div>
                                                </div>

                                                <div style={{ padding: '15px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {category.permissions.map(perm => (
                                                        <label key={perm} style={{
                                                            fontSize: '0.75em', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer',
                                                            background: selectedPermissions.includes(perm) ? '#28a745' : '#333',
                                                            display: 'flex', alignItems: 'center', gap: '6px'
                                                        }}>
                                                            <input type="checkbox" checked={selectedPermissions.includes(perm)} onChange={() => togglePermission(perm)} />
                                                            <code>{perm}</code>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {Object.keys(permissionGroups).length > 0 && (
                                    <div style={{ marginTop: '40px' }}>
                                        <h4 style={{ marginBottom: '15px' }}>⚙️ Zusätzliche Berechtigungen</h4>
                                        {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                                            <div key={groupName} style={{ marginBottom: '25px' }}>
                                                <h5 style={{ borderBottom: '1px solid #444', paddingBottom: '8px', color: '#007bff' }}>{groupName}</h5>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                                                    {groupPerms.map((perm, idx) => {
                                                        const pName = typeof perm === 'string' ? perm : perm.name;
                                                        const isSelected = selectedPermissions.includes(pName);
                                                        return (
                                                            <label key={pName || idx} style={{
                                                                padding: '10px', borderRadius: '6px', border: `1px solid ${isSelected ? '#007bff' : '#444'}`,
                                                                background: isSelected ? 'rgba(0,123,255,0.1)' : '#1a1a1a', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px'
                                                            }}>
                                                                <input type="checkbox" checked={isSelected} onChange={() => togglePermission(pName)} style={{ marginTop: '3px' }} />
                                                                <div>
                                                                    <div style={{ fontSize: '0.85em', fontWeight: isSelected ? 'bold' : 'normal' }}>{pName}</div>
                                                                    {perm.description && <div style={{ fontSize: '0.7em', color: '#666' }}>{perm.description}</div>}
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryPermissions;